import React, { useState, useEffect } from "react";
import MCategoryActionBar from "./MCActionBar/ActionBar";
import DynamicTable from "../../../components/Table/MTable";
import Header from "../../../components/Header/Header";
import Footer from "../../../components/Footer/Footer";
import axios from "axios";
import { useSelector } from "react-redux";
import { Upload } from "lucide-react";

const MCategory = () => {
  const [categories, setCategories] = useState([]);
  const [filteredCategories, setFilteredCategories] = useState([]);
  const [selectedRow, setSelectedRow] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importError, setImportError] = useState("");
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const apiEndpoint = `${API_BASE_URL}/mcategory/mcategories`;
  const { role } = useSelector((state) => state.auth);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const itemsPerPage = 10;

  // Add export options state
  const [exportOptions, setExportOptions] = useState({
    showOptions: false,
    format: null,
  });

  const columns = [
    { key: "CategoryID", label: "Category ID" },
    { key: "CategoryName", label: "Category Name" },
    { key: "NormalDays", label: "Normal Days" },
    { key: "EmergencyDays", label: "Emergency Days" },
  ];

  const fetchCategories = async (page = 1, query = "") => {
    try {
      const response = await axios.get(
        `${apiEndpoint}?page=${page}&limit=${itemsPerPage}&search=${query}`
      );
      setCategories(response.data.items);
      setFilteredCategories(response.data.items);
      setTotalItems(response.data.total);
      setTotalPages(Math.ceil(response.data.total / itemsPerPage));
      setCurrentPage(page);
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  // Add function to handle export
  const handleExport = async (format) => {
    setExportOptions({ showOptions: true, format });
  };

  // Add function to execute export
  const executeExport = async (exportType) => {
    try {
      let url = `${apiEndpoint}/export?format=${exportOptions.format}`;
      
      if (exportType === 'current') {
        url += `&page=${currentPage}&limit=${itemsPerPage}`;
      } else if (exportType === 'search' && searchQuery) {
        url += `&search=${searchQuery}`;
      }
      // For 'all', we don't add any parameters

      const response = await axios.get(url, { responseType: 'blob' });
      const blob = new Blob([response.data], { 
        type: exportOptions.format === 'csv' 
          ? 'text/csv' 
          : 'application/vnd.oasis.opendocument.spreadsheet' 
      });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `categories_export.${exportOptions.format}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Error exporting data:", error);
      alert("Error exporting data. Please try again.");
    } finally {
      setExportOptions({ showOptions: false, format: null });
    }
  };

  const handleFileImport = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);
    setImporting(true);
    setImportError("");

    try {
      await axios.post(`${apiEndpoint}/import`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        }
      });
      await fetchCategories(1, searchQuery);
      alert("File imported successfully!");
    } catch (error) {
      setImportError(error.response?.data?.error || "Error importing file");
    } finally {
      setImporting(false);
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    fetchCategories(page, searchQuery);
  };

  const handleSearch = async (query) => {
    setSearchQuery(query);
    setCurrentPage(1); // Reset to first page when searching
    await fetchCategories(1, query);
  };

  useEffect(() => {
    fetchCategories(1, searchQuery);
  }, []);

  return (
    <div className="min-h-screen flex flex-col">
      <Header 
        exportEndpoint={apiEndpoint} 
        onExport={handleExport}
      />

      {exportOptions.showOptions && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-lg">
            <h3 className="text-lg font-semibold mb-4">Export Options</h3>
            <div className="space-y-3">
              <button
                className="w-full px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                onClick={() => executeExport('current')}
              >
                Export Current Page
              </button>
              {searchQuery && (
                <button
                  className="w-full px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                  onClick={() => executeExport('search')}
                >
                  Export Search Results
                </button>
              )}
              <button
                className="w-full px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                onClick={() => executeExport('all')}
              >
                Export All Data
              </button>
              <button
                className="w-full px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
                onClick={() => setExportOptions({ showOptions: false, format: null })}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="flex-grow p-4">
        <div className="flex justify-between items-center mb-4">
          <MCategoryActionBar
            title="Categories"
            rowKey="CategoryID"
            deleteMessage="Are you sure you want to remove this category permanently?"
            fields={[
              { name: "CategoryName", label: "Category Name", required: true },
              { name: "NormalDays", label: "Normal Days", type: "number" },
              { name: "EmergencyDays", label: "Emergency Days", type: "number" },
            ]}
            apiEndpoint={apiEndpoint}
            data={filteredCategories}
            selectedRow={selectedRow}
            onRowSelect={setSelectedRow}
            onRefresh={() => fetchCategories(currentPage, searchQuery)}
            onSearch={handleSearch}
            userRole="admin"
          />
          
          <div className="flex items-center">
            <label className="relative cursor-pointer bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center">
              <Upload className="mr-2" size={20} />
              {importing ? "Importing..." : "Import CSV/ODS"}
              <input
                type="file"
                className="hidden"
                accept=".csv,.ods"
                onChange={handleFileImport}
                disabled={importing}
              />
            </label>
            {importError && (
              <div className="text-red-500 ml-2">{importError}</div>
            )}
          </div>
        </div>

        <div className="mt-6 mb-2 text-center">
          <h3 className="text-xl font-semibold">Category Table</h3>
        </div>

        <div className="mt-4">
          <DynamicTable
            columns={columns}
            data={filteredCategories}
            selectedRow={selectedRow}
            onRowSelect={setSelectedRow}
            rowKey="CategoryID"
            highlightColor="bg-green-100"
            noDataMessage="No categories found."
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
            totalItems={totalItems}
            itemsPerPage={itemsPerPage}
          />
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default MCategory;